import { useState, useEffect, useCallback } from "react";
import { getPageContentBySlug } from "@/actions";
import {
  parseText,
  parseJsonOrArray,
  getImageUrl,
  isVideoUrl,
} from "../our-technology/our-technology.hook";
import {
  BioMaterialImg,
  biomassInsightImg,
  letsTalkImg,
} from "@/assets/images";

export { parseText, parseJsonOrArray, getImageUrl, isVideoUrl };

export const useHome = (initialPageData = null, slug = "home") => {
  const [data, setData] = useState(initialPageData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [livePreviewData, setLivePreviewData] = useState(null);

  const fetchPageContent = useCallback(
    async (targetSlug = slug) => {
      setLoading(true);
      setError(null);
      try {
        const res = await getPageContentBySlug(targetSlug);
        if (res?.success && res?.data) {
          setData(res.data);
          return res.data;
        }
      } catch (err) {
        console.error(`Error fetching home page content for (${targetSlug}):`, err);
        setError(err?.message || "Failed to fetch home page content");
      } finally {
        setLoading(false);
      }
    },
    [slug]
  );

  useEffect(() => {
    if (!initialPageData) {
      fetchPageContent(slug);
    } else {
      setData(initialPageData);
    }
  }, [initialPageData, slug, fetchPageContent]);

  useEffect(() => {
    const handlePreviewMessage = (event) => {
      if (event.data?.type === "SUSTANIX_PREVIEW_DATA") {
        setLivePreviewData(event.data.data);
      }
    };
    window.addEventListener("message", handlePreviewMessage);
    return () => window.removeEventListener("message", handlePreviewMessage);
  }, []);

  const pageContent = data?.page?.page_content || {};
  const imageUrlBase = data?.imageUrl || "";

  const heroPhotoRaw =
    livePreviewData?.heroCoverPhoto ||
    pageContent.heroCoverPhoto ||
    pageContent.cover_photo ||
    pageContent.heroPhoto ||
    pageContent.image;
  const heroPhoto = getImageUrl(heroPhotoRaw, imageUrlBase);

  const section2PhotoRaw =
    livePreviewData?.section2Photo ||
    pageContent.section2Photo ||
    pageContent.section2_photo;
  const section2Photo =
    getImageUrl(section2PhotoRaw, imageUrlBase) || BioMaterialImg;

  const section4PhotoRaw =
    livePreviewData?.section4Photo ||
    livePreviewData?.sec4Photo ||
    pageContent.section4Photo ||
    pageContent.sec4Photo ||
    pageContent.section4_photo;
  const section4Photo =
    getImageUrl(section4PhotoRaw, imageUrlBase) || biomassInsightImg;

  const section7PhotoRaw =
    livePreviewData?.section7Photo ||
    livePreviewData?.sec7Photo ||
    pageContent.section7Photo ||
    pageContent.sec7Photo ||
    pageContent.section7_photo;
  const section7Photo =
    getImageUrl(section7PhotoRaw, imageUrlBase) || letsTalkImg;

  const raw3Cards =
    parseJsonOrArray(livePreviewData?.section3Cards) ||
    parseJsonOrArray(pageContent.section3Cards || pageContent.section3_cards);
  const formatted3Cards = Array.isArray(raw3Cards)
    ? raw3Cards.map((item, idx) => ({
        ...item,
        id: item.id || idx + 1,
        title: parseText(item.title || item.name),
        desc: parseText(item.desc || item.description),
        icon: getImageUrl(item.icon, imageUrlBase) || item.icon,
      }))
    : null;

  const raw5Cards =
    parseJsonOrArray(livePreviewData?.section5Cards) ||
    parseJsonOrArray(livePreviewData?.sec5CardsStates) ||
    parseJsonOrArray(pageContent.section5Cards || pageContent.section5_cards);
  const formatted5Cards = Array.isArray(raw5Cards)
    ? raw5Cards.map((item, idx) => ({
        ...item,
        id: item.id || idx + 1,
        title: parseText(item.title || item.titleState || item.name),
        desc: parseText(item.desc || item.descriptionState || item.description),
        photo: getImageUrl(item.photo || item.img || item.image, imageUrlBase),
      }))
    : null;

  const rawRow1Logos =
    parseJsonOrArray(livePreviewData?.section6Row1Logos || livePreviewData?.sec6Raw1Logos) ||
    parseJsonOrArray(pageContent.section6Row1Logos || pageContent.section6_row1_logos);
  const formattedRow1Logos = Array.isArray(rawRow1Logos)
    ? rawRow1Logos
        .map((item) => {
          const logoSrc = typeof item === "string" ? item : item?.logo || item?.image;
          return getImageUrl(logoSrc, imageUrlBase);
        })
        .filter(Boolean)
    : null;

  const rawRow2Logos =
    parseJsonOrArray(livePreviewData?.section6Row2Logos || livePreviewData?.sec6Raw2Logos) ||
    parseJsonOrArray(pageContent.section6Row2Logos || pageContent.section6_row2_logos);
  const formattedRow2Logos = Array.isArray(rawRow2Logos)
    ? rawRow2Logos
        .map((item) => {
          const logoSrc = typeof item === "string" ? item : item?.logo || item?.image;
          return getImageUrl(logoSrc, imageUrlBase);
        })
        .filter(Boolean)
    : null;

  const activeContent = {
    // Hero
    title1: livePreviewData?.title1 || parseText(pageContent.title1) || "Truly circular",
    title2: livePreviewData?.title2 || parseText(pageContent.title2) || "PFAS free",
    title3: livePreviewData?.title3 || parseText(pageContent.title3) || "Microplastic free",
    title4: livePreviewData?.title4 || parseText(pageContent.title4) || "Bio-based",
    title5: livePreviewData?.title5 || parseText(pageContent.title5 || pageContent.description || pageContent.sub_title) || "materials",
    heroCoverPhoto: heroPhoto || "/video/combined_video.mp4",
    heroIsVideo: isVideoUrl(
      heroPhotoRaw || "/video/combined_video.mp4",
      livePreviewData?.heroIsVideo ?? (pageContent.heroIsVideo || pageContent.is_video || true)
    ),

    // Section 2: Bio materials
    section2Title:
      livePreviewData?.section2Title ||
      parseText(pageContent.section2Title || pageContent.section2_title),
    section2Description:
      livePreviewData?.section2Description ||
      parseText(pageContent.section2Description || pageContent.section2_description) ||
      "We research and manufacture novel bio-based molecules to make polymers & formulations that replace fossil-based materials. Industries can move towards greener solution without compromise on economics or scale.",
    section2DrawerTitle:
      livePreviewData?.section2DrawerTitle ||
      parseText(pageContent.section2DrawerTitle || pageContent.section2_drawer_title),
    section2DrawerDescription:
      livePreviewData?.section2DrawerDescription ||
      parseText(pageContent.section2DrawerDescription || pageContent.section2_drawer_description),
    section2Photo,
    section2CallActions:
      parseJsonOrArray(livePreviewData?.section2CallActions) ||
      parseJsonOrArray(pageContent.section2CallActions || pageContent.section2_call_actions),

    // Section 3: Fundamentals
    section3Title:
      livePreviewData?.section3Title ||
      parseText(pageContent.section3Title || pageContent.section3_title),
    section3Cards: formatted3Cards,

    // Section 4: Biomass insight
    section4Title:
      livePreviewData?.section4Title ||
      livePreviewData?.sec4Title ||
      parseText(pageContent.section4Title || pageContent.sec4Title || pageContent.section4_title) ||
      "Biomass is the new oil...",
    section4Description:
      livePreviewData?.section4Description ||
      livePreviewData?.sec4Description ||
      parseText(pageContent.section4Description || pageContent.sec4Description || pageContent.section4_description) ||
      "adding value to this nature’s ingredient",
    section4DrawerTitle:
      livePreviewData?.section4DrawerTitle ||
      parseText(pageContent.section4DrawerTitle || pageContent.section4_drawer_title),
    section4DrawerDescription:
      livePreviewData?.section4DrawerDescription ||
      parseText(pageContent.section4DrawerDescription || pageContent.section4_drawer_description),
    section4Photo,

    // Section 5: Impact
    section5Title:
      livePreviewData?.section5Title ||
      parseText(pageContent.section5Title || pageContent.section5_title),
    section5Cards: formatted5Cards,

    // Section 6: Marquee Logos
    section6Row1Logos: formattedRow1Logos,
    section6Row2Logos: formattedRow2Logos,

    // Section 7: Let's talk
    section7Title:
      livePreviewData?.section7Title ||
      livePreviewData?.sec7Title ||
      parseText(pageContent.section7Title || pageContent.sec7Title || pageContent.section7_title),
    section7Photo,
    section7IsVideo: isVideoUrl(
      section7PhotoRaw,
      livePreviewData?.section7IsVideo ?? livePreviewData?.sec7IsVideo ?? (pageContent.section7IsVideo || pageContent.is_video || false)
    ),
    section7CallActions:
      parseJsonOrArray(livePreviewData?.section7CallActions || livePreviewData?.sec7CallActions) ||
      parseJsonOrArray(pageContent.section7CallActions || pageContent.section7_call_actions),
  };

  return {
    data,
    setData,
    loading,
    error,
    livePreviewData,
    activeContent,
    imageUrlBase,
    fetchPageContent,
    getImageUrl: (src) => getImageUrl(src, imageUrlBase),
    isVideoUrl,
    parseText,
    parseJsonOrArray,
  };
};

export default useHome;

